"use client";

import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

type TriggerKind = "activity" | "location";

type UseExclusiveSearchFilterTriggerArgs = {
  triggerId: string;
  triggerKind: TriggerKind;
  isOpen: boolean;
  activeTrigger: string | null;
  initialTrigger: TriggerKind | null;
  setTriggerOpen: (id: string, isOpen: boolean) => void;
  setInitialTrigger: (trigger: TriggerKind | null) => void;
  onSyncOpen?: () => void;
  onDeactivateWhileOpen?: () => void;
};

export function useExclusiveSearchFilterTrigger({
  triggerId,
  triggerKind,
  isOpen,
  activeTrigger,
  initialTrigger,
  setTriggerOpen,
  setInitialTrigger,
  onSyncOpen,
  onDeactivateWhileOpen,
}: UseExclusiveSearchFilterTriggerArgs) {
  const prevIsOpenRef = useRef(isOpen);
  const prevActiveTriggerRef = useRef(activeTrigger);

  // Keep SearchTriggerContext in sync when an externally controlled selector opens.
  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;

    if (isOpen && !wasOpen) {
      onSyncOpen?.();
      setTriggerOpen(triggerId, true);
      if (initialTrigger === null) {
        setInitialTrigger(triggerKind);
      }
    }
  }, [
    isOpen,
    initialTrigger,
    onSyncOpen,
    setInitialTrigger,
    setTriggerOpen,
    triggerId,
    triggerKind,
  ]);

  // Close if this selector was active and another selector becomes active.
  useLayoutEffect(() => {
    const wasActive = prevActiveTriggerRef.current === triggerId;
    prevActiveTriggerRef.current = activeTrigger;

    if (wasActive && activeTrigger !== triggerId && isOpen) {
      onDeactivateWhileOpen?.();
    }
  }, [activeTrigger, isOpen, onDeactivateWhileOpen, triggerId]);

  const openTrigger = useCallback(() => {
    onSyncOpen?.();
    setTriggerOpen(triggerId, true);
    if (initialTrigger === null) {
      setInitialTrigger(triggerKind);
    }
  }, [
    initialTrigger,
    onSyncOpen,
    setInitialTrigger,
    setTriggerOpen,
    triggerId,
    triggerKind,
  ]);

  return {
    openTrigger,
  } as const;
}
